import {
  authenticatedFetch,
  extractApiErrorMessage,
  resolvePeriverseApiBaseUrl,
} from '../shared';
import {
  AssetCatalogFilterParams,
  AssetCatalogItemDto,
  AssetCatalogItemDtoSchema,
  CreateAssetCatalogDto,
  PaginatedAssetCatalogResponseDto,
  PaginatedAssetCatalogResponseDtoSchema,
  UpdateAssetCatalogDto,
} from '@peritasai/types';

type BaseUrlResolver = () => string;

export class AssetCatalogApiClient {
  constructor(
    private readonly resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
  ) {}

  async getAssetCatalog(
    options: AssetCatalogFilterParams = {}
  ): Promise<PaginatedAssetCatalogResponseDto> {
    const queryParams = new URLSearchParams();

    if (options.search) {
      queryParams.append('search', options.search);
    }

    if (options.name) {
      queryParams.append('name', options.name);
    }
    
    if (options.type) {
      queryParams.append('type', options.type);
    }
    if (options.isActive !== undefined) {
      queryParams.append('isActive', String(options.isActive));
    }

    const page = options.page !== undefined ? options.page + 1 : 1;
    const limit = options.limit ?? 10;
    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());

    const url = `${this.resolveBaseUrl()}/asset-catalog?${queryParams.toString()}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: { accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return PaginatedAssetCatalogResponseDtoSchema.parse(await response.json());
  }

  async createAssetCatalogItem(
    dto: CreateAssetCatalogDto
  ): Promise<AssetCatalogItemDto> {
    const url = `${this.resolveBaseUrl()}/asset-catalog`;

    const response = await authenticatedFetch(url, {
      method: 'POST',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(dto),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return AssetCatalogItemDtoSchema.parse(await response.json());
  }

  async updateAssetCatalogItem(
    id: string,
    dto: UpdateAssetCatalogDto
  ): Promise<AssetCatalogItemDto> {
    const url = `${this.resolveBaseUrl()}/asset-catalog/${id}`;

    const response = await authenticatedFetch(url, {
      method: 'PATCH',
      headers: { accept: 'application/json', 'content-type': 'application/json' },
      body: JSON.stringify(dto),
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return AssetCatalogItemDtoSchema.parse(await response.json());
  }

  async getAssetCatalogItem(id: string): Promise<AssetCatalogItemDto> {
    const baseUrl = this.resolveBaseUrl();
    const url = `${baseUrl}/asset-catalog/${id}`;

    const response = await authenticatedFetch(url, {
      method: 'GET',
      headers: { accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error(await extractApiErrorMessage(response));
    }

    return AssetCatalogItemDtoSchema.parse(await response.json());
  }
}

export const createAssetCatalogApiClient = (
  resolveBaseUrl: BaseUrlResolver = resolvePeriverseApiBaseUrl
): AssetCatalogApiClient => new AssetCatalogApiClient(resolveBaseUrl);

export const assetCatalogApiClient = new AssetCatalogApiClient();

export type { BaseUrlResolver };
